import {
  Banner,
  Button,
  FieldSwitch,
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { unwrapResult } from '@reduxjs/toolkit';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import EmojiOne from 'emojione';
import { accessControl } from 'lib-shared-access-control';
import { electron } from 'lib-shared-electron';
import {
  DraftCustomAttributeValue,
  LinkedConversationTypes,
  MessagetextContentType,
  Queue,
  Tag,
} from 'lib-shared-legacy-types';
import { compareSemVer } from 'lib-shared-semver';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import noop from 'lodash/noop';
import React, { FormEvent } from 'react';
import { Field, FieldArray, InjectedFormProps, reduxForm } from 'redux-form';
import sanitizeHtml from 'sanitize-html';

import { getSubject } from 'app/utils/conversationTitle';
import markdownToHTML from 'app/utils/markdownToHTML';
import { getMessagesTranscript } from 'app/utils/messages';
import { LanguageCode } from 'app/utils/supportedLanguages';
import { AppLink } from 'components/appLink';
import { CopilotPinnedActions } from 'components/copilotPinnedActions';
import { ExpandText } from 'components/copilotPinnedActions/components/expandText';
import { ImproveReply } from 'components/copilotPinnedActions/components/improveReply';
import { MatchToStyleGuide } from 'components/copilotPinnedActions/components/matchToStyleGuide';
import { SpellingAndGrammar } from 'components/copilotPinnedActions/components/spellingAndGrammar';
import { EditorCopilotDropdown } from 'components/editorCopilotDropdown';
import { ModalCopilotActions } from 'components/editorCopilotDropdown/utils/composeModalCopilotActions';
import { EditorMentions } from 'components/editorMentions';
import { EditorQuickRepliesDropdown } from 'components/editorQuickRepliesDropdown';
import { SatisfactionButton } from 'components/shared/satisfactionButton/SatisfactionButton';
import { SatisfactionDisabledButton } from 'components/shared/satisfactionButton/components/SatisfactionDisabledButton';
import { ValidationState } from 'components/shared/util/richTextField/components/components/stateBar/stateBar.types';
import { QueueSelectField } from 'fable/queueSelect';
import { FieldToggleBoxHead, ToggleBox, ToggleBoxBody } from 'fable/toggleBox';
import { getEmailIntegrationLabel } from 'utils/emailIntegration';
import { ServerStorageProps } from 'utils/useServerStorage';

import InputField from '../../util/InputField';
import SelectField from '../../util/SelectField';
import { TagsSelectFieldArray } from '../../util/TagsSelectFieldArray';
import { getRequestID } from '../../util/request';
import RichTextField from '../../util/richTextField/components/richTextField';
import AttachmentsFieldList from '../../util/richTextField/containers/utils/attachmentsFieldList';
import ListUserEmailOptions from '../../util/userField/ListUserEmailOptions';
import {
  EmailAddressWithOptionalName,
  EmailPayload,
  SaveDraftPayload,
  SendEmailResponseT,
} from '../api/composeEmail';
import ComposeEmailHelper from '../helper/ComposeEmailHelper';
import styles from '../styles/composeEmail.module.scss';

import { ComposeCustomAttributesToggle } from './ComposeCustomAttributesField';
import {
  ComposeEmailModalErrorType,
  DispatchProps,
  StateProps,
} from './ComposeEmailModalContainer';
import { asyncValidate, validate } from './ComposeEmailModalValidation';
import ComposeWindowPortal from './ComposeWindowPortal';
import UserItem from './UserItem';

import '../styles/compose-email.less';

/** The redux-form name. */
export const FORM_NAME = 'composeEmailModal';

/** The electron version supporting pop-outs */
const DESKTOP_APP_SUPPORT_POP_OUT_VERSION = '3.3.11';

/** The email user. */
export type EmailUser = { user: User & { display_name?: string | null } };

/** The data bound to the redux-form. */
export interface ComposeEmailModalFormValues {
  parentConversation?: number;
  toUser?: EmailUser[];
  subject?: string;
  message?: string;
  internalNote?: string;
  attachments?: any[];
  to?: EmailUser[];
  cc?: EmailUser[];
  bcc?: EmailUser[];
  fromEmails?: string;
  linkConversationType?: LinkedConversationTypes;
  messageContentType?: MessagetextContentType;
  timestamp?: any;
  tags?: Tag[];
  queue?: Queue;
  offer?: boolean;
  internalNoteOpen?: boolean;
  customAttributes?: Record<string, DraftCustomAttributeValue>;
  customAttributesOpen?: boolean;
}

type WrappedComponentProps = {
  onRetrievalOfEditorText: (receivedEditorText: string) => void;
} & ModalCopilotActions;

export type ComposeEmailModalProps = StateProps &
  DispatchProps &
  ServerStorageProps<'organization', 'organizationStorage'> &
  WrappedComponentProps;

type ComposeEmailModalState = {
  ccVisible: boolean;
  bccVisible: boolean;
  isSubjectLoading: boolean;
  showWindowPortal: boolean;
  userFieldFilled: boolean;
  supportsPopoutCompose: boolean;
  cancelledSatisfaction: boolean;
  satisfactionLanguage?: LanguageCode;
  messageValidation: ValidationState;
  users: [];
  allEmailsUsers: (User & { duplicate?: boolean })[];
  uploadingAttachmentsControlCount: number;
};

type ComposeEmailModalPropsWithReduxForm = InjectedFormProps<
  ComposeEmailModalFormValues,
  ComposeEmailModalProps,
  ComposeEmailModalErrorType
> &
  ComposeEmailModalProps;

const satisfactionLanguageFallback = 'en';

const INITIAL_STATE: ComposeEmailModalState = {
  ccVisible: true,
  bccVisible: true,
  isSubjectLoading: false,
  userFieldFilled: false,
  showWindowPortal: false,
  supportsPopoutCompose: true,
  cancelledSatisfaction: false,
  messageValidation: { touched: false, valid: false },
  users: [],
  allEmailsUsers: [],
  uploadingAttachmentsControlCount: 0,
};

/** Gets the list of recipients (cc & bcc) */
const getRecipients = (
  emailUsers: EmailUser[],
): EmailAddressWithOptionalName[] => {
  const recipients: EmailAddressWithOptionalName[] = [];

  for (const { user } of emailUsers) {
    const recipient: EmailAddressWithOptionalName = {
      email: user.email as string,
    };
    if (
      user.display_name &&
      typeof user.display_name === 'string' &&
      user.display_name.trim() !== ''
    ) {
      recipient.name = user.display_name;
    }
    recipients.push(recipient);
  }

  return recipients;
};

const getEmailPayload = ({
  toEmail,
  contentType,
  formValues,
  language,
}: {
  toEmail: EmailPayload['to_email'];
  contentType: EmailPayload['content_type'];
  formValues: ComposeEmailModalFormValues;
  language: LanguageCode | undefined;
}) => {
  const payload: EmailPayload = {
    from_email: formValues.fromEmails as string,
    to_email: toEmail,
    content_type: contentType,
    subject: formValues.subject as string,
    body: formValues.message as string,
  };

  if (formValues.cc && formValues.cc.length > 0) {
    payload.cc = getRecipients(formValues.cc);
  }
  if (formValues.bcc && formValues.bcc.length > 0) {
    payload.bcc = getRecipients(formValues.bcc);
  }
  if (formValues.tags && formValues.tags.length > 0) {
    payload.tags = formValues.tags;
  }
  if (formValues.queue) {
    payload.queueId = formValues.queue.id;
  }
  if (formValues.offer !== undefined) {
    payload.offer = formValues.offer;
  }
  if (language) {
    payload.language = language;
  }
  if (formValues.parentConversation) {
    payload.parentConversation = formValues.parentConversation;
  }
  if (formValues.linkConversationType) {
    payload.linkType = formValues.linkConversationType;
  }

  if (formValues.internalNote && formValues.internalNoteOpen) {
    payload.internalNote = formValues.internalNote;
  }

  if (
    formValues.attachments !== undefined &&
    Array.isArray(formValues.attachments) === true
  ) {
    const attachments: EmailPayload['attachments_v2'] = [];
    for (const attachment of formValues.attachments) {
      if (attachment !== undefined) {
        attachments.push({
          pretty_name: attachment.name,
          url: attachment.relative_uri,
        });
      }
    }
    payload.attachments_v2 = attachments;
  }

  return payload;
};

export class ComposeEmailModal extends React.Component<
  ComposeEmailModalPropsWithReduxForm,
  ComposeEmailModalState
> {
  private containerRef = React.createRef<HTMLDivElement>();
  private messageEditorRef = React.createRef<any>();
  private internalNoteEditorRef = React.createRef<any>();

  constructor(props: ComposeEmailModalPropsWithReduxForm) {
    super(props);
    this.state = INITIAL_STATE;
  }

  /**
   * Clears custom attributes from the form state. Used when closing the modal,
   * sending email, or opening a fresh compose.
   */
  clearCustomAttributes = (): void => {
    this.props.change('customAttributes', undefined);
    this.props.change('customAttributesOpen', undefined);
  };

  /**
   * Handles changes to custom attributes from the ComposeCustomAttributesToggle
   * component.
   */
  handleCustomAttributesChange = (
    values: Record<string, DraftCustomAttributeValue>,
  ): void => {
    this.props.change('customAttributes', values);
  };

  async componentDidMount() {
    window.addEventListener('unload', this.handleUnload);

    if (electron.isElectron()) {
      const version = await electron.getVersion();
      const supportsPopoutCompose =
        compareSemVer(version, DESKTOP_APP_SUPPORT_POP_OUT_VERSION) !== -1;
      this.setState({ supportsPopoutCompose });
    }
  }

  componentDidUpdate(prevProps: ComposeEmailModalProps) {
    const { ui, initialValues } = this.props;

    if (!prevProps.ui.isModalOpen && ui.isModalOpen) {
      this.props.composeEmailGetAllFrom(null);

      // Clear custom attributes for fresh compose (no draft timestamp)
      // This prevents stale values from a previous session from showing
      if (!initialValues?.timestamp) {
        this.clearCustomAttributes();
      }

      this.setState({
        userFieldFilled: !!initialValues?.toUser,
      });

      if (this.props.fromEmails.length === 1) {
        this.props.change('fromEmails', this.props.fromEmails[0].email_address);
      }

      if (initialValues?.message) {
        let message = initialValues.message;

        if (initialValues.messageContentType === 'markdown') {
          message = markdownToHTML(EmojiOne.shortnameToUnicode(message))
            .replace(/<p>/g, '<div>')
            .replace(/<\/p>/g, '</div><br />');
        }

        this.props.change('message', message);
      }

      if (initialValues?.internalNote) {
        this.props.change('internalNoteOpen', true);
        this.props.change('internalNote', initialValues.internalNote);
      }

      if (initialValues && initialValues.fromEmails) {
        this.props.change('fromEmails', initialValues.fromEmails);
      }

      if (initialValues?.cancelledSatisfaction) {
        this.setState({
          cancelledSatisfaction: initialValues.cancelledSatisfaction,
        });
      }

      this.setState({
        satisfactionLanguage:
          initialValues.satisfactionLanguage ||
          this.props.metaConversation?.conversation?.attributes?.language,
      });
    }
  }

  static getDerivedStateFromProps(
    props: ComposeEmailModalPropsWithReduxForm,
    state: ComposeEmailModalState,
  ) {
    if (
      props.users !== state.users &&
      (props.users.length !== state.users.length ||
        props.users.some(
          (nextUser: User) =>
            !state.users.find((user: User) => nextUser.id === user.id),
        ))
    ) {
      return {
        users: props.users,
        allEmailsUsers: props.users.flatMap((user) => {
          if (user.additional_emails) {
            return [
              user,
              ...user.additional_emails.map((email) => ({
                ...user,
                email,
                duplicate: true,
              })),
            ];
          }

          return user;
        }),
      };
    }

    return null;
  }

  @boundMethod
  handleUnload(): void {
    this.closeWindowPortal();
  }

  messageSent = (
    { data: { csid: newConversationId } }: SendEmailResponseT,
    parentConversation: ComposeEmailModalFormValues['parentConversation'],
  ) => {
    const notificationID = getRequestID();
    this.setMessageValidationState({ touched: false, valid: false });

    const leftAction = {
      text: 'Go to conversation',
      action: {
        navigate: `/conversation/${newConversationId}`,
      },
    };
    const rightAction = {
      text: 'Dismiss',
      action: () => {
        this.props.notificationRemoveById({ id: notificationID }); // Remove the notification.
      },
    };

    const notificationOptions = {
      id: notificationID,
      type: 'messageSent',
      title: 'New message sent',
      text: `Message was sent`,
      icon: 'i-done',
      className: 'success',
      time: 10000,
      showDesktop: false,
      leftAction: leftAction,
      rightAction: rightAction,
    };

    if (
      this.props.initialValues !== null &&
      this.props.initialValues.timestamp != null
    ) {
      // We need to delete the old draft.
      this.props.composeEmailRemoveDraft({
        timestamp: this.props.initialValues.timestamp,
      });
    }

    this.props.notificationAdd(notificationOptions);

    // We make the new conversation a child conversation to a parent conversation.
    if (parentConversation) {
      this.props.conversationLinkChild({
        csid: newConversationId,
      });
    }

    // Save custom attributes to the newly created conversation
    const { formValues, hasFFCustomAttributesInComposeEmail } = this.props;
    if (
      hasFFCustomAttributesInComposeEmail &&
      formValues?.customAttributes &&
      Object.keys(formValues.customAttributes).length > 0
    ) {
      this.props
        .saveConversationCustomAttributes(
          newConversationId,
          formValues.customAttributes,
        )
        .catch((error: Error) => {
          // Log error but don't block the user - email was already sent successfully
          console.warn('Failed to save custom attributes:', error);
        });
    }

    this.clearCustomAttributes();

    this.props.destroy();
    this.props.composeEmailCloseModal();
  };

  handleFormSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    const { uploadingAttachmentsControlCount } = this.state;

    // Prevent sending while files are uploading
    if (uploadingAttachmentsControlCount > 0) {
      return;
    }

    this.handleSend();
  };

  handleSend = (): void => {
    const { hasFFWriteHTMLEmail, formValues } = this.props;

    const contentType: MessagetextContentType = hasFFWriteHTMLEmail
      ? 'html'
      : null;

    const messagePlain = this.messageEditorRef?.current?.getPlainText();

    if (!messagePlain || messagePlain.length < 1) {
      this.setMessageValidationState({
        touched: true,
        valid: false,
        error: { type: 'required' },
      });
      return;
    }

    const language: LanguageCode | undefined = this.state.cancelledSatisfaction
      ? undefined
      : this.state.satisfactionLanguage;

    const showErrorNotification = (email: string, e: Error) => {
      const notificationOptions = {
        type: 'messageNotSent',
        title: 'An error occurred',
        text: `Some error occurred - message was not sent to ${email}`,
        icon: 'i-exclamation',
        className: 'warning',
        showDesktop: false,
      };
      this.props.notificationAdd(notificationOptions);
    };

    // Do we need to add the user before we sent the message.
    const toUser = formValues.toUser?.[0]?.user;

    if (toUser?.id) {
      const payload = getEmailPayload({
        toEmail: toUser.email,
        contentType,
        formValues,
        language,
      });
      this.setMessageValidationState({ touched: true, valid: true });
      this.props
        .sendEmail(payload)
        .then((response: SendEmailResponseT) => {
          this.messageSent(response, formValues.parentConversation);
          this.closeWindowPortal();
        })
        .catch((e: any) => {
          showErrorNotification(toUser.email as string, e);
          console.info(e.data);
          console.warn('Add user exception', e);
        });
    } else {
      // We do not have the user id for this user.
      // We need to create the user.
      const user: any = {
        email: formValues.toUser?.[0].user.email,
        user_type: 'contact',
      };

      if (toUser?.display_name) {
        user.name = toUser.display_name;
      }

      // Create user
      this.props
        .userModalAddUser(user)
        .then(unwrapResult)
        .then(({ data: newUser }) => {
          const payload: EmailPayload = getEmailPayload({
            toEmail: newUser.email,
            contentType,
            formValues,
            language,
          });
          // Send email message
          this.props
            .sendEmail(payload)
            .then((response: SendEmailResponseT) => {
              // Show notification and remove draft if any
              this.messageSent(response, formValues.parentConversation);
            })
            .catch((e: Error) => {
              showErrorNotification(user.email as string, e);
              console.warn('Add user exception', e);
            });
        })
        .catch((e: Error) => {
          showErrorNotification(user.email as string, e);
          console.warn('Add user exception', e);
        });
    }
  };

  @boundMethod
  getTemplatesRenderContext(): Record<string, string> {
    const { formValues, organization, profile } = this.props;

    const user = {
      name: formValues?.toUser?.[0]?.user?.name || '',
      email: formValues?.toUser?.[0]?.user?.email || '',
    };

    const displayName = formValues?.toUser?.[0]?.user?.display_name;
    if (displayName !== undefined && displayName !== null) {
      user.name = displayName;
    }

    return {
      organization_name: sanitizeHtml(organization.name),
      requester_firstname: sanitizeHtml(
        user.name.substr(0, user.name.indexOf(' ')) || user.name,
      ),
      requester_fullname: sanitizeHtml(user.name),
      requester_email: sanitizeHtml(user.email),
      agent_firstname: sanitizeHtml(profile.given_name ?? ''),
      agent_lastname: sanitizeHtml(profile.family_name ?? ''),
      agent_fullname: sanitizeHtml(
        `${profile.given_name ?? ''} ${profile.family_name ?? ''}`,
      ),
      agent_displayname: sanitizeHtml(profile.name ?? ''),
      conversation_subject: sanitizeHtml(
        formValues && formValues.subject ? formValues.subject : '',
      ),
    };
  }

  subjectRender = (subject: string): Promise<string> => {
    if (!subject) {
      return Promise.reject('No subject given');
    }

    if (subject && subject.trim() === '') {
      return Promise.reject('No subject given');
    }

    return this.props
      .templatesRender({
        context: this.getTemplatesRenderContext(),
        content: subject,
      })
      .then((resolve: { data: { rendered_content: string } }) => {
        return resolve.data.rendered_content;
      })
      .catch((exception: any) => {
        console.warn('templatesRender subject Exception', exception);
        return subject || '';
      });
  };

  formSubjectHasContent = (): boolean => {
    const { formValues } = this.props;
    return !(
      !formValues ||
      !formValues.subject ||
      (formValues && formValues.subject && formValues.subject.trim() === '')
    );
  };

  changeSubject = (subject: string, force = false): void => {
    if (subject && (force || !this.formSubjectHasContent())) {
      this.props.change('subject', subject);
    }
  };

  saveDraft = (): void => {
    const { satisfactionLanguage, cancelledSatisfaction } = this.state;
    const {
      formValues,
      initialValues,
      composeEmailSaveDraft,
      composeEmailCloseModal,
      destroy,
    } = this.props;

    if (formValues) {
      const draft: SaveDraftPayload = {};
      if (formValues.fromEmails) {
        draft.fromEmails = formValues.fromEmails;
      }
      if (
        formValues.toUser &&
        Array.isArray(formValues.toUser) &&
        formValues.toUser.length > 0 &&
        formValues.toUser[0].user
      ) {
        draft.toUser = formValues.toUser as EmailUser[];
      }
      if (
        formValues.cc &&
        Array.isArray(formValues.cc) &&
        formValues.cc.length > 0 &&
        formValues.cc[0].user
      ) {
        draft.cc = formValues.cc;
      }
      if (
        formValues.bcc &&
        Array.isArray(formValues.bcc) &&
        formValues.bcc.length > 0 &&
        formValues.bcc[0].user
      ) {
        draft.bcc = formValues.bcc;
      }
      if (formValues.subject) {
        draft.subject = formValues.subject;
      }
      if (formValues.message) {
        draft.message = formValues.message;
      }
      if (formValues.internalNote && formValues.internalNoteOpen) {
        draft.internalNote = formValues.internalNote;
      }
      if (
        formValues.attachments &&
        Array.isArray(formValues.attachments) &&
        formValues.attachments.length > 0
      ) {
        const attachments: Array<any> = [];
        for (const attachment of formValues.attachments) {
          if (attachment) {
            attachments.push(attachment);
          }
        }
        draft.attachments = attachments;
      }
      if (formValues.tags) {
        draft.tags = formValues.tags;
      }
      if (formValues.linkConversationType) {
        draft.linkConversationType = formValues.linkConversationType;
      }
      if (formValues.messageContentType) {
        draft.messageContentType = formValues.messageContentType;
      }
      if (formValues.queue) {
        draft.queue = formValues.queue;
      }
      if (formValues.offer) {
        draft.offer = formValues.offer;
      }

      if (satisfactionLanguage) {
        draft.satisfactionLanguage = satisfactionLanguage;
      }

      if (cancelledSatisfaction) {
        draft.cancelledSatisfaction = cancelledSatisfaction;
      }

      // Parent Conversation for linked conversation.
      if (formValues.parentConversation) {
        draft.parentConversation = formValues.parentConversation;
      }

      // Custom attributes (only save if feature flag is enabled)
      if (this.props.hasFFCustomAttributesInComposeEmail) {
        if (
          formValues.customAttributes &&
          Object.keys(formValues.customAttributes).length > 0
        ) {
          draft.customAttributes = formValues.customAttributes;
        }
        if (formValues.customAttributesOpen) {
          draft.customAttributesOpen = formValues.customAttributesOpen;
        }
      }

      composeEmailSaveDraft(draft)
        .then(() => {
          if (this.props.initialValues?.timestamp) {
            // We need to delete the old draft.
            this.props.composeEmailRemoveDraft({
              timestamp: initialValues.timestamp,
            });
          }

          //this.props.notificationAdd(notificationOptions);
          this.clearCustomAttributes();
          destroy();
          composeEmailCloseModal();
          this.closeWindowPortal();
        })
        .catch((exception: any) => {
          console.warn('exception', exception);
        });
    }
  };

  minimizeModal = (): void => {
    const { composeEmailCloseModal, destroy, formValues, pristine } =
      this.props;
    if (this.state.showWindowPortal) {
      this.closeWindowPortal();
    }
    if (formValues && !pristine) {
      this.saveDraft();
    } else {
      this.clearCustomAttributes();
      destroy();
      this.setMessageValidationState({ touched: false, valid: false });
      composeEmailCloseModal();
    }
  };

  handleMessageRichTextChange = (text: string): void => {
    this.props.change('message', text);
    this.props.onRetrievalOfEditorText(text);
  };

  handleInternalNoteRichTextChange = (text: string): void => {
    this.props.change('internalNote', text);
  };

  handleFileIsUploadingChange = (isUploadingAttachments: boolean): void => {
    const uploadingAttachmentsControlCount = Math.max(
      0,
      this.state.uploadingAttachmentsControlCount +
        (isUploadingAttachments ? 1 : -1),
    );

    this.setState({ uploadingAttachmentsControlCount });
  };

  handleFromEmailChange = (value: string): void => {
    this.props
      .updateLastUsedEmailIntegration({
        key: 'lastUsedEmailIntegration',
        value,
      })
      .catch((e) => {
        console.log(e);
      });
  };

  setMessageValidationState = (validation: ValidationState): void => {
    this.setState({ messageValidation: validation });
  };

  toggleCC = (): void => {
    this.setState({ ccVisible: !this.state.ccVisible });
  };

  toggleBCC = (): void => {
    this.setState({ bccVisible: !this.state.bccVisible });
  };

  hideField = (targetName: string): void => {
    if (targetName === 'Cc') {
      this.setState({ ccVisible: true });
    } else if (targetName === 'Bcc') {
      this.setState({ bccVisible: true });
    }
  };

  copyPasteParentConversationClickHandler = (): void => {
    const {
      metaConversation,
      formValues,
      hasFFWriteHTMLEmail,
      rewrittenHtmlEmailMessages,
    } = this.props;
    const linebreak = `<br /><br />`;
    const { transcript: transcriptRaw, attachments } = getMessagesTranscript(
      metaConversation,
      rewrittenHtmlEmailMessages,
      hasFFWriteHTMLEmail,
    );
    const conversation = metaConversation?.conversation;

    let transcript = `${linebreak}${linebreak}${transcriptRaw}`;

    // Set message from parent conversation and concat with already typed text.
    const currentText = formValues.message || '';
    if (currentText) {
      transcript = `${linebreak} ${transcript}`;
    }
    this.messageEditorRef.current?.setText(currentText + transcript);

    // Set subject (only if there not already typed a subject).
    if (conversation?.attributes && formValues) {
      const subject = getSubject(conversation?.attributes || null);
      if (
        subject &&
        (!formValues ||
          !formValues.subject ||
          (formValues &&
            formValues.subject &&
            formValues.subject.trim() === ''))
      ) {
        this.props.change('subject', subject);
      }
    }

    const isInstagram =
      conversation?.initial_channel === 'facebookmessenger' &&
      !!conversation?.attributes?.instagram_page_id &&
      !!conversation?.attributes?.instagram_profile_id;

    // Add attachments from parent and concat with already added.
    if (attachments.length > 0 && !isInstagram) {
      if (formValues && formValues.attachments) {
        this.props.change(
          'attachments',
          attachments.concat(formValues.attachments),
        );
      } else {
        this.props.change('attachments', attachments);
      }
    } else if (formValues && formValues.attachments) {
      // For instagram side conversation use only existing form attachments
      // reasoning: https://dixa-product.atlassian.net/jira/software/projects/AX/boards/94?selectedIssue=AX-1105
      this.props.change('attachments', formValues.attachments);
    } else {
      //  Form instagram side conversation no attachments to add
      this.props.change('attachments', []);
    }
  };

  subjectOnBlurAction = (e: Event): Promise<void> =>
    this.subjectRender((e.target as HTMLInputElement).value)
      .then((renderedSubject) => {
        this.changeSubject(renderedSubject, true);
      })
      .catch(noop);

  @boundMethod
  subjectOnKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.messageEditorRef.current?.focus();
    }
  }

  @boundMethod
  closeWindowPortal(): void {
    ComposeEmailHelper.setWindow(window);
    this.setState({
      showWindowPortal: false,
    });
  }

  @boundMethod
  toggleWindowPortal(): void {
    this.setState({
      showWindowPortal: !this.state.showWindowPortal,
    });
  }

  closeAndDestroyDraft = (): void => {
    const { destroy, composeEmailCloseModal } = this.props;
    if (this.state.showWindowPortal) {
      this.closeWindowPortal();
    }
    this.setCCsVisibility(true);
    this.clearCustomAttributes();
    destroy();
    composeEmailCloseModal();
  };

  setCCsVisibility = (ccState: boolean): void => {
    this.setState({ ccVisible: ccState, bccVisible: ccState });
  };

  setCCsVisible = (): void => {
    this.setCCsVisibility(true);
  };

  @boundMethod
  handleEditorSubmit(): void {
    const { uploadingAttachmentsControlCount } = this.state;

    // Prevent sending while files are uploading
    if (uploadingAttachmentsControlCount > 0) {
      return;
    }

    if (this.props.valid) {
      this.handleSend();
    }
  }

  @boundMethod
  renderElementItem(props: any): JSX.Element {
    return <UserItem {...props} />;
  }

  render() {
    let isLoading = false;
    let btnText = 'Send';
    let noEmailIntegration: JSX.Element | null = null;
    let linkedConversationLead: JSX.Element | null = null;
    let composeHeader = 'Compose new email';

    const {
      supportsPopoutCompose,
      showWindowPortal,
      cancelledSatisfaction,
      satisfactionLanguage,
      messageValidation,
      uploadingAttachmentsControlCount,
    } = this.state;

    const {
      meta,
      ui,
      fromEmails,
      initialValues,
      formValues,
      metaConversation,
      touch,
      composeEmailCloseModal,
      destroy,
    } = this.props;

    const hasTagAccess = accessControl.hasAccess('conversation.tag');
    const hasIntegrationSetupAccess = accessControl.hasAccess(
      'settings.integration.channel.setup',
    );

    if (
      meta.send === 'loading' ||
      meta.findUser === 'loading' ||
      meta.getAllFrom === 'loading' ||
      meta.getPresignedUrl === 'loading' ||
      uploadingAttachmentsControlCount > 0
    ) {
      isLoading = true;
    }

    if (meta.send === 'loading') {
      btnText = 'Sending';
    } else if (meta.send === 'failed') {
      btnText = 'Failed - try again';
    } else if (uploadingAttachmentsControlCount > 0) {
      btnText = 'Uploading';
    }

    const listFromMails = (fromEmail: any, index: number) => {
      return (
        <option value={fromEmail.email_address} key={index}>
          {getEmailIntegrationLabel(fromEmail)}
        </option>
      );
    };

    if (fromEmails.length === 0 && meta.getAllFrom === 'succeeded') {
      noEmailIntegration = (
        <Banner
          className={styles.banner}
          variant="warning"
          icon={null}
          description={
            <Text.Paragraph as="div">
              No email integration found.{' '}
              {hasIntegrationSetupAccess && (
                <Text.ExternalLink>
                  <AppLink
                    path="/settings/email"
                    onClick={() => {
                      this.clearCustomAttributes();
                      destroy();
                      composeEmailCloseModal();
                    }}
                  >
                    Create one in email settings
                  </AppLink>
                </Text.ExternalLink>
              )}
            </Text.Paragraph>
          }
        />
      );
    }

    if (initialValues && initialValues.parentConversation) {
      if (initialValues.linkConversationType === 'email_forward') {
        // Forward conversation.
        linkedConversationLead = (
          <Banner
            className={styles.banner}
            variant="info"
            icon={null}
            description={
              <Text.Paragraph>
                Compose a transcript conversation of #
                {initialValues.parentConversation}
              </Text.Paragraph>
            }
          />
        );
        composeHeader = `Compose transcript conversation of #${initialValues.parentConversation}`;
      } else if (initialValues.linkConversationType === 'side') {
        // Linked side conversation.
        linkedConversationLead = (
          <Banner
            className={styles.banner}
            variant="info"
            icon={null}
            description={
              <FlexBox flexDirection="column" gap="200">
                <Text.Paragraph>
                  Compose a side conversation to #
                  {initialValues.parentConversation}
                </Text.Paragraph>

                {!!metaConversation && (
                  <Button
                    onClick={this.copyPasteParentConversationClickHandler}
                  >
                    Copy & Paste from conversation #
                    {initialValues.parentConversation}
                  </Button>
                )}
              </FlexBox>
            }
          />
        );
        composeHeader = `Compose side conversation to #${initialValues.parentConversation}`;
      } else if (initialValues.linkConversationType === 'follow_up') {
        composeHeader = `Follow-up on Email for Chat #${initialValues.parentConversation} - with ${initialValues.toUser?.[0].user.email}`;
      }
    }

    const RenderComponent = showWindowPortal ? ComposeWindowPortal : Modal;
    const renderCompProps: any = {};

    if (supportsPopoutCompose && showWindowPortal) {
      renderCompProps.title = 'Compose email';
      renderCompProps.copyStyles = true;
      renderCompProps.onUnload = this.closeWindowPortal;
      renderCompProps.onBlock = this.closeWindowPortal;
      renderCompProps.name = '_modal';
      renderCompProps.url = '';

      renderCompProps.features = {
        width: 900,
        height: 710,
        directories: false,
        titlebar: false,
        toolbar: false,
        location: false,
        status: false,
        menubar: false,
        nodeIntegration: false,
        nativeWindowOpen: true,
      };
      renderCompProps.onOpen = (window) => {
        ComposeEmailHelper.setWindow(window);
        // Force a re-render to update editorWindow prop with the popup window reference.
        // This will ensure that the inline images paths are updated in the popup window.
        this.forceUpdate();
      };
    } else {
      renderCompProps.isOpen = ui.isModalOpen;
      renderCompProps.onClose = this.minimizeModal;
    }

    return (
      <RenderComponent {...renderCompProps}>
        <div
          className={classnames({
            'compose-email-modal': true,
            'compose-email-modal--popout': showWindowPortal,
          })}
          id="compose-email-modal"
        >
          <form onSubmit={this.handleFormSubmit}>
            <ModalHead
              title={composeHeader}
              onClose={this.minimizeModal}
              secondaryButton={
                supportsPopoutCompose
                  ? {
                      onClick: this.toggleWindowPortal,
                      tooltipContent: showWindowPortal
                        ? 'Show in main window'
                        : 'Open in new window',
                      icon: !showWindowPortal ? 'external-link' : 'compress',
                    }
                  : undefined
              }
            >
              {/* FIXME: [TEAMS-6670]  */}
              {/* {!this.props.pristine && (
                <label className={'unsaved-changes-label label label-danger'}>
                  Unsaved changes
                </label>
              )} */}
            </ModalHead>
            <ModalBody>
              {noEmailIntegration}
              {linkedConversationLead}
              <Field name="parentConversation" component="hidden" />
              <Field name="linkConversationType" component="hidden" />
              <FieldArray
                name="toUser"
                required={true}
                singleChoice={true}
                fieldLabel="To"
                touch={touch}
                component={ListUserEmailOptions as any}
                elementItem={this.renderElementItem}
                availableOptions={this.state.allEmailsUsers}
                status={this.props.meta.findUser}
                getfunction={this.props.composeEmailFindUser}
                placeholder="Search for email or name..."
                focus={!this.state.userFieldFilled}
              >
                <span className="cc-bcc-container">
                  <Button
                    type="button"
                    variant="tertiary"
                    onClick={this.toggleCC}
                  >
                    {this.state.ccVisible === true ? '+' : '-'}Cc
                  </Button>
                  <Button
                    type="button"
                    variant="tertiary"
                    onClick={this.toggleBCC}
                  >
                    {this.state.bccVisible === true ? '+' : '-'}Bcc
                  </Button>
                </span>
              </FieldArray>

              {!this.state.ccVisible && (
                <FieldArray
                  name="cc"
                  required={false}
                  singleChoice={false}
                  fieldLabel="Cc"
                  touch={touch}
                  component={ListUserEmailOptions as any}
                  elementItem={this.renderElementItem}
                  hideField={this.hideField}
                  availableOptions={this.state.allEmailsUsers}
                  status={this.props.meta.findUser}
                  getfunction={this.props.composeEmailFindUser}
                  placeholder="Search for email or name..."
                />
              )}

              {!this.state.bccVisible && (
                <FieldArray
                  name="bcc"
                  required={false}
                  singleChoice={false}
                  fieldLabel="Bcc"
                  touch={touch}
                  component={ListUserEmailOptions as any}
                  elementItem={this.renderElementItem}
                  hideField={this.hideField}
                  availableOptions={this.state.allEmailsUsers}
                  status={this.props.meta.findUser}
                  getfunction={this.props.composeEmailFindUser}
                  placeholder="Search for email or name..."
                />
              )}

              <Field
                onChangeAction={this.handleFromEmailChange}
                displayName="From"
                required={true}
                name="fromEmails"
                component={SelectField}
              >
                {fromEmails.length > 1 && (
                  <option hidden value="">
                    {' '}
                    - Please select a from email
                  </option>
                )}
                {fromEmails.map(listFromMails)}
              </Field>
              <div className="subject-csat">
                <div className="subject">
                  <Field
                    displayName="Subject"
                    autoFocus={this.state.userFieldFilled}
                    required={true}
                    name="subject"
                    showSpinner={this.state.isSubjectLoading}
                    onBlurAction={this.subjectOnBlurAction}
                    onKeyDownAction={this.subjectOnKeyDown}
                    component={InputField}
                    type="text"
                    placeholder="Subject..."
                  />
                </div>
                {initialValues?.linkConversationType === 'follow_up' && (
                  <>
                    {cancelledSatisfaction && (
                      <div className="csat">
                        <SatisfactionDisabledButton text="Cancelled" />
                      </div>
                    )}
                    {!cancelledSatisfaction &&
                      formValues?.fromEmails != null && (
                        <div className="csat">
                          <SatisfactionButton
                            channel="email"
                            contactPoint={formValues.fromEmails}
                            selected={
                              satisfactionLanguage ??
                              satisfactionLanguageFallback
                            }
                            isCancellable={false}
                            onChange={(satisfactionLanguage) =>
                              this.setState({ satisfactionLanguage })
                            }
                            onCancel={() =>
                              this.setState({ cancelledSatisfaction: true })
                            }
                          />
                        </div>
                      )}
                  </>
                )}
              </div>
              <div
                className="richtext-wrap"
                data-testid="compose-richtext-message"
              >
                <RichTextField
                  label="Message"
                  placeholder="Type your message"
                  className="message-richtext-editor"
                  channel="email"
                  ref={this.messageEditorRef}
                  value={formValues?.message}
                  onChange={this.handleMessageRichTextChange}
                  onFileIsUploadingChange={this.handleFileIsUploadingChange}
                  validation={messageValidation}
                  setValidationState={this.setMessageValidationState}
                  attachments={formValues?.attachments ?? []}
                  attachmentsFields={
                    <FieldArray
                      name="attachments"
                      component={AttachmentsFieldList}
                    />
                  }
                  required
                  form={FORM_NAME}
                  editorWindow={ComposeEmailHelper.getWindow()}
                  onTriggerSend={this.handleEditorSubmit}
                  toolbarAddons={
                    <>
                      <EditorQuickRepliesDropdown
                        enduserId={formValues?.toUser?.[0]?.user?.id || null}
                        changeSubject={this.changeSubject}
                        customWindow={ComposeEmailHelper.getWindow()}
                        popoverContent={{ side: 'bottom', align: 'start' }}
                      />
                      <EditorCopilotDropdown
                        editorCopilotActions={this.props.editorCopilotActions}
                        channel="email"
                      />
                      <CopilotPinnedActions
                        channel="email"
                        text={formValues?.message ?? ''}
                        pinnableActions={[
                          {
                            id: 'improveReply',
                            component: ImproveReply,
                            condition: (args) =>
                              args.flags.agentCopilotPinActions &&
                              args.pinState['improveReply'],
                            label: 'Improve message',
                            disabledImprovements: ['match_tone'],
                          },
                          {
                            id: 'improveReplyMatchStyleGuide',
                            component: MatchToStyleGuide,
                            condition: (args) =>
                              args.flags.agentCopilotPinActions &&
                              args.pinState['improveReplyMatchStyleGuide'],
                          },
                          {
                            id: 'checkSpellingAndGrammar',
                            component: SpellingAndGrammar,
                            condition: (args) =>
                              args.flags.agentCopilotPinActions &&
                              args.pinState['checkSpellingAndGrammar'],
                          },
                          {
                            id: 'expandText',
                            component: ExpandText,
                            condition: (args) =>
                              args.flags.agentCopilotPinActions &&
                              args.pinState['expandText'],
                            label: 'Expand message',
                          },
                        ]}
                      />
                    </>
                  }
                />
              </div>
              <ToggleBox
                className={classnames('internal-note', {
                  'internal-note-open': !!formValues?.internalNoteOpen,
                })}
                headComponent={FieldToggleBoxHead}
                isOpen={!!formValues?.internalNoteOpen}
                title="Include an internal note"
                input={{
                  id: 'internalNoteOpen',
                  value: 'internalNoteOpen',
                  name: 'internalNoteOpen',
                }}
                onChange={(isOpen) => {
                  isOpen && ampli.followUpConversationModalInternalNoteAdded();
                }}
              >
                <ToggleBoxBody>
                  <div
                    className={styles.editorContainer}
                    ref={this.containerRef}
                  >
                    <RichTextField
                      className={styles.editor}
                      placeholder="Type your internal note"
                      channel="notes"
                      ref={this.internalNoteEditorRef}
                      value={formValues?.internalNote}
                      form={FORM_NAME}
                      editorWindow={ComposeEmailHelper.getWindow()}
                      onChange={this.handleInternalNoteRichTextChange}
                      onFileIsUploadingChange={this.handleFileIsUploadingChange}
                      onTriggerSend={this.handleEditorSubmit}
                      toolbarAddons={
                        <>
                          <EditorMentions container={this.containerRef} />
                          <EditorQuickRepliesDropdown
                            enduserId={
                              formValues?.toUser?.[0]?.user?.id || null
                            }
                            customWindow={ComposeEmailHelper.getWindow()}
                            popoverContent={{ side: 'bottom', align: 'start' }}
                          />
                          <EditorCopilotDropdown
                            editorCopilotActions={
                              this.props.internalNoteEditorCopilotActions
                            }
                            channel="email"
                          />
                          <CopilotPinnedActions
                            channel="email"
                            text={
                              this.internalNoteEditorRef.current?.getText() ??
                              ''
                            }
                            pinnableActions={[
                              {
                                id: 'improveReply',
                                component: ImproveReply,
                                condition: (args) =>
                                  args.flags.agentCopilotPinActions &&
                                  args.pinState['improveReply'],
                                label: 'Improving note',
                                disabledImprovements: ['match_tone'],
                              },
                              {
                                id: 'checkSpellingAndGrammar',
                                component: SpellingAndGrammar,
                                condition: (args) =>
                                  args.flags.agentCopilotPinActions &&
                                  args.pinState['checkSpellingAndGrammar'],
                              },
                            ]}
                          />
                        </>
                      }
                    />
                  </div>
                </ToggleBoxBody>
              </ToggleBox>
              {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                <Tooltip
                  content="You don't have permissions to add tags"
                  disabled={hasTagAccess}
                >
                  <FieldArray
                    name="tags"
                    component={TagsSelectFieldArray}
                    disabled={!hasTagAccess}
                  />
                </Tooltip>
              }
              {this.props.hasFFCustomAttributesInComposeEmail && (
                <ComposeCustomAttributesToggle
                  value={
                    formValues?.customAttributes ??
                    initialValues?.customAttributes
                  }
                  onChange={this.handleCustomAttributesChange}
                  queueId={formValues?.queue?.id}
                  isOpen={
                    formValues?.customAttributesOpen ??
                    initialValues?.customAttributesOpen
                  }
                />
              )}
              <div className="queue-offer">
                <div className="queue">
                  <Field
                    className="queue"
                    name="queue"
                    component={QueueSelectField}
                  />
                </div>
                <div className="offer">
                  <label>
                    <span>Offer in queue</span>
                    <Field
                      name="offer"
                      type="checkbox"
                      component={FieldSwitch}
                      onChange={() =>
                        ampli.followUpConversationModalOfferInQueueAdded()
                      }
                    />
                  </label>
                </div>
              </div>
            </ModalBody>
            <ModalFoot
              cancelOnClick={this.closeAndDestroyDraft}
              cancelName="Cancel"
              confirmSecondaryOnClick={this.saveDraft}
              confirmSecondaryName="Save draft and close"
              confirmSecondaryProps={{
                className: styles.modalConfirmSecondaryButton,
              }}
              className={styles.modalButtons}
            >
              <Button
                type="submit"
                variant={meta.send === 'failed' ? 'danger' : 'primary'}
                disabled={isLoading}
                isLoading={isLoading}
                onClick={this.setCCsVisible}
              >
                {btnText}
              </Button>
            </ModalFoot>
          </form>
        </div>
      </RenderComponent>
    );
  }
}

export default reduxForm<
  ComposeEmailModalFormValues,
  ComposeEmailModalProps,
  ComposeEmailModalErrorType
>({
  form: FORM_NAME,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  validate,
  asyncValidate,
})(ComposeEmailModal);
